import React, { useState } from "react";
import { SakuraFlower } from "./SakuraDecorations";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
    website_url: "", // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Navn er påkrævet";
    if (!formData.phone.trim()) newErrors.phone = "Telefonnummer er påkrævet";
    if (!formData.email.trim()) {
      newErrors.email = "Email er påkrævet";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Ugyldig email-adresse";
    }
    if (!formData.message.trim()) newErrors.message = "Besked er påkrævet";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          message: "",
          website_url: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (err) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="glass-card rounded-3xl p-6 sm:p-8 border border-[#ffe0cf] shadow-[0_16px_40px_rgba(89,52,39,0.08)] relative overflow-hidden">
      {/* Decorative Header */}
      <div className="flex items-center justify-between pb-5 mb-6 border-b border-[#ffe0cf]">
        <div className="flex items-center space-x-2.5">
          <h3 className="font-serif text-lg sm:text-xl font-semibold text-[#593427]">
            Send en besked til klinikken
          </h3>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Honeypot field for antispam - hidden from humans */}
        <div className="sr-only absolute opacity-0 pointer-events-none -z-10" aria-hidden="true">
          <label htmlFor="website_url">Website URL (lad dette felt stå tomt)</label>
          <input
            type="text"
            id="website_url"
            name="website_url"
            tabIndex="-1"
            value={formData.website_url}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>

        {submitStatus === "success" && (
          <div className="p-4 rounded-2xl bg-[#e1ef9f]/40 border border-[#e1ef9f] text-[#593427] text-sm flex items-start space-x-3">
            <i className="fa-solid fa-circle-check text-[#d74814] text-lg mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-semibold">Mange tak for din henvendelse!</p>
              <p className="mt-0.5">Jeg har modtaget din besked og vender tilbage til dig snarest muligt.</p>
            </div>
          </div>
        )}

        {submitStatus === "error" && (
          <div className="p-4 rounded-2xl bg-red-50/90 border border-red-200 text-red-800 text-sm flex items-start space-x-3">
            <i className="fa-solid fa-circle-exclamation text-red-600 text-lg mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-semibold">Der opstod en fejl ved afsendelse.</p>
              <p className="mt-0.5">
                Prøv venligst igen, eller kontakt mig direkte på tlf.{" "}
                <a href="tel:29387186" className="underline font-semibold whitespace-nowrap">
                  29 38 71 86
                </a>
                .
              </p>
            </div>
          </div>
        )}

        {/* Navn */}
        <div>
          <label htmlFor="name" className="block text-xs font-semibold text-[#593427] uppercase tracking-wide mb-1">
            Navn <span className="text-[#d74814]">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className={`w-full px-4 py-3 rounded-xl border bg-white/95 text-[#593427] placeholder-stone-400 text-sm focus:outline-none focus:ring-2 backdrop-blur-sm ${
              errors.name
                ? "border-red-400 focus:ring-red-300"
                : "border-[#ffe0cf] focus:border-[#d74814] focus:ring-[#d74814]/20"
            }`}
          />
          {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
        </div>

        {/* Telefon og Email i grid på større skærme */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold text-[#593427] uppercase tracking-wide mb-1">
              Telefon <span className="text-[#d74814]">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="F.eks. 29 38 71 86"
              className={`w-full px-4 py-3 rounded-xl border bg-white/95 text-[#593427] placeholder-stone-400 text-sm focus:outline-none focus:ring-2 backdrop-blur-sm ${
                errors.phone
                  ? "border-red-400 focus:ring-red-300"
                  : "border-[#ffe0cf] focus:border-[#d74814] focus:ring-[#d74814]/20"
              }`}
            />
            {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-[#593427] uppercase tracking-wide mb-1">
              Email <span className="text-[#d74814]">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="din@email.dk"
              className={`w-full px-4 py-3 rounded-xl border bg-white/95 text-[#593427] placeholder-stone-400 text-sm focus:outline-none focus:ring-2 backdrop-blur-sm ${
                errors.email
                  ? "border-red-400 focus:ring-red-300"
                  : "border-[#ffe0cf] focus:border-[#d74814] focus:ring-[#d74814]/20"
              }`}
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>
        </div>

        {/* Besked */}
        <div>
          <label htmlFor="message" className="block text-xs font-semibold text-[#593427] uppercase tracking-wide mb-1">
            Besked / Beskrivelse <span className="text-[#d74814]">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            required
            value={formData.message}
            onChange={handleChange}
            placeholder="Beskriv hvad du ønsker hjælp til eller spørgsmål vedr. behandling..."
            className={`w-full px-4 py-3 rounded-xl border bg-white/95 text-[#593427] placeholder-stone-400 text-sm focus:outline-none focus:ring-2 resize-y backdrop-blur-sm ${
              errors.message
                ? "border-red-400 focus:ring-red-300"
                : "border-[#ffe0cf] focus:border-[#d74814] focus:ring-[#d74814]/20"
            }`}
          />
          {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
        </div>

        {/* Submit Button with Sakura Icon */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-[#d74814] text-white font-medium text-sm hover:bg-[#b83a0d] transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#d74814] focus:ring-offset-2 disabled:opacity-50 group"
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden="true" />
                <span>Sender besked...</span>
              </>
            ) : (
              <>
                <SakuraFlower className="w-4 h-4 mr-2 transition-transform group-hover:rotate-45" />
                <span>Send besked</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
